import { useCallback, useEffect, useState } from 'react';
import { Button, Select } from '@forecastify/ui';
import {
  oauthConsentRequestSchema,
  oauthConsentResultSchema,
  OAUTH_PATHS,
  type OauthConsentRequest,
} from '@forecastify/shared';
import { useT } from '../../i18n';
import { useSession } from '../../session';
import { AuthFrame } from '../auth/auth-frame';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C52 · the consent screen. `decisions.md` R-44                             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ```
 * ┌───────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THIS SCREEN IS OURS AND MUST BE. IT IS THE ONLY PLACE A PERSON    │
 * │ EVER AGREES THAT SOMEBODY ELSE'S SOFTWARE MAY ACT AS THEM.            │
 * └───────────────────────────────────────────────────────────────────────┘
 * ```
 *
 * R-44: per-client consent, stored server-side and checked **before** any redirect. A consent in a
 * cookie can be replayed by a page the person never visited; a consent inferred from *"they are
 * signed in"* is not a consent at all.
 *
 * ⚠️⚠️ **THE CLIENT'S NAME AND THE REDIRECT HOST ARE SHOWN SEPARATELY, BECAUSE ONLY ONE OF THEM IS
 * TRUSTWORTHY.** *"Authorization servers MUST clearly display the redirect URI hostname during
 * authorization."* `clientName` is whatever the client's own metadata document says — it can claim
 * to be anything. The host is where the authorization code will actually be delivered, and it is the
 * one fact on this page an attacker cannot forge.
 *
 * ⚠️ **And a loopback-only client gets a warning**, which the specification asks for in as many
 * words: *"Client ID Metadata Documents cannot prevent `localhost` URL impersonation by
 * themselves … authorization servers SHOULD display additional warnings for `localhost`-only
 * redirect URIs."* Any application on the machine can listen on a loopback port.
 *
 * ⚠️ **No CSRF token, and that is structural rather than an omission.** An approval here is an
 * authenticated XHR carrying a bearer token this app holds **in memory** (R-11). CSRF needs ambient
 * authority — a cookie the browser attaches to a cross-site request — and there is none to borrow.
 * `oauth.controller.ts` makes the same argument from the other end.
 */

type State =
  | { readonly state: 'loading' }
  | { readonly state: 'failed'; readonly code: string }
  | { readonly state: 'ready'; readonly request: OauthConsentRequest };

export function OauthConsentScreen() {
  const t = useT();
  const session = useSession();

  /**
   * ⚠️ **Read once into state**, like `verify-email-screen.tsx`: the value is the capability for
   * this whole flow, and re-reading `location.search` on a later render is how a page ends up
   * disagreeing with itself.
   */
  const [handle] = useState<string>(
    () => new URLSearchParams(window.location.search).get('request') ?? '',
  );

  const [screen, setScreen] = useState<State>({ state: 'loading' });
  const [workspaceId, setWorkspaceId] = useState<string>('');
  const [deciding, setDeciding] = useState(false);

  const read = useCallback(async (): Promise<void> => {
    if (handle === '') {
      setScreen({ state: 'failed', code: 'not_found' });
      return;
    }

    const response = await session.request(OAUTH_PATHS.consent(handle)).catch(() => null);
    if (!response?.ok) {
      setScreen({ state: 'failed', code: 'not_found' });
      return;
    }

    const parsed = oauthConsentRequestSchema.safeParse(await response.json());
    /* ⚠️ A 200 that fails the contract is a FAILURE, not a blank page: an approval screen that
     * rendered half of what it was given would be asking somebody to agree to something it could
     * not describe. */
    if (!parsed.success) {
      setScreen({ state: 'failed', code: 'unknown' });
      return;
    }

    setScreen({ state: 'ready', request: parsed.data });
    setWorkspaceId(parsed.data.workspaces[0]?.id ?? '');
  }, [handle, session]);

  useEffect(() => {
    /* ⚠️ Only once signed in. `Gate` sends a signed-out person to the sign-in screen and back, so
     * this effect simply waits rather than racing it. */
    if (session.status !== 'signed-in') return;
    void read();
  }, [read, session.status]);

  const decide = async (approve: boolean): Promise<void> => {
    setDeciding(true);
    const response = await session
      .request(OAUTH_PATHS.consent(handle), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(approve ? { approve, workspaceId } : { approve }),
      })
      .catch(() => null);

    if (!response?.ok) {
      setDeciding(false);
      setScreen({ state: 'failed', code: 'not_found' });
      return;
    }

    const parsed = oauthConsentResultSchema.safeParse(await response.json());
    if (!parsed.success) {
      setDeciding(false);
      setScreen({ state: 'failed', code: 'unknown' });
      return;
    }

    /**
     * ⚠️⚠️ **`location.replace`, NOT `assign`, AND NOT `<Link>`.** The destination is the client's
     * own address and belongs to somebody else's software — react-router has no business with it.
     * `replace` keeps the consent screen out of the Back stack, so pressing Back does not return to
     * a page whose request has already been consumed and would now read as an error.
     */
    window.location.replace(parsed.data.redirectTo);
  };

  if (session.status !== 'signed-in') {
    return (
      <AuthFrame screen="oauth-consent" title={t('consent.title')}>
        <p className="text-[13px] leading-relaxed">{t('consent.signIn')}</p>
      </AuthFrame>
    );
  }

  if (screen.state === 'loading') {
    return (
      <AuthFrame screen="oauth-consent" title={t('consent.title')}>
        <p role="status" className="text-muted-foreground text-[13px]">
          {t('consent.loading')}
        </p>
      </AuthFrame>
    );
  }

  if (screen.state === 'failed') {
    return (
      <AuthFrame screen="oauth-consent" title={t('consent.failed.title')}>
        <p className="text-[13px] leading-relaxed">{t('consent.failed.body')}</p>
      </AuthFrame>
    );
  }

  const request = screen.request;

  return (
    <AuthFrame screen="oauth-consent" title={t('consent.title')}>
      <div className="flex flex-col gap-4">
        <p className="text-[13px] leading-relaxed">
          {t('consent.lede', { client: request.clientName })}
        </p>

        {/**
         * ⚠️⚠️ **THE HOST, IN ITS OWN ROW AND NOT INSIDE A SENTENCE.** A hostname buried in prose is
         * a hostname nobody reads, and it is the only line on this page that an attacker controlling
         * `clientName` cannot also control.
         */}
        <dl className="border-border flex flex-col gap-2 rounded-md border p-3 text-[12.5px]">
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-muted-foreground">{t('consent.field.client')}</dt>
            <dd className="font-medium">{request.clientName}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-muted-foreground">{t('consent.field.redirect')}</dt>
            <dd className="font-medium tabular-nums">{request.redirectHost}</dd>
          </div>
        </dl>

        {request.localhostOnly && (
          /* ⚠️ The specification's own SHOULD. Any application on this machine can listen on a
             loopback port, so the host tells somebody far less here than it usually does. */
          <p role="note" className="text-[12.5px] leading-relaxed">
            {t('consent.localhost')}
          </p>
        )}

        <div className="flex flex-col gap-1.5">
          <p className="text-muted-foreground text-[12px]">{t('consent.scopes')}</p>
          <ul className="flex list-disc flex-col gap-1 pl-5 text-[12.5px]">
            {request.scopes.map((scope) => (
              <li key={scope}>{t(scopeKey(scope))}</li>
            ))}
          </ul>
        </div>

        {/**
         * ⚠️ **The workspace is chosen here and nowhere else.** A grant is per workspace (`0124`), so
         * agreeing that a chat client may act in one is not agreeing that it may act in another —
         * and a screen that picked silently would be making that decision for somebody.
         */}
        <label className="flex flex-col gap-1.5 text-[12.5px]">
          <span className="text-muted-foreground">{t('consent.workspace')}</span>
          {/* ⚠️ The app's own `Select` and never a bare `<select>` — CLAUDE.md's first settled
              control. A native one is drawn by the operating system and takes no CSS at all. */}
          <Select
            value={workspaceId}
            onChange={(event) => {
              setWorkspaceId(event.target.value);
            }}
            options={request.workspaces.map((workspace) => ({
              value: workspace.id,
              label: workspace.name,
            }))}
          />
        </label>

        {request.alreadyGranted && (
          <p className="text-muted-foreground text-[12px]">{t('consent.again')}</p>
        )}

        <div className="flex flex-col gap-2">
          <Button
            size="lg"
            className="w-full"
            disabled={deciding || workspaceId === ''}
            onClick={() => {
              void decide(true);
            }}
          >
            {t('consent.approve')}
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="w-full"
            disabled={deciding}
            onClick={() => {
              void decide(false);
            }}
          >
            {t('consent.decline')}
          </Button>
        </div>

        {/* ⚠️ Last, in small type, and it is the sentence that makes the gate legible: whatever this
            application asks for, a write still stops at a card somebody presses in Forecastify. */}
        <p className="text-muted-foreground text-[11.5px] leading-relaxed">{t('consent.gate')}</p>
      </div>
    </AuthFrame>
  );
}

/** ⚠️ One literal key per scope, because the catalogue's completeness check reads literals. */
function scopeKey(
  scope: string,
): 'consent.scope.read' | 'consent.scope.write' | 'consent.scope.contracts' {
  if (scope === 'forecastify:write') return 'consent.scope.write';
  if (scope === 'forecastify:contracts') return 'consent.scope.contracts';
  return 'consent.scope.read';
}
